import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { Cookie } from 'ng2-cookies/cookie';
import { AuthenticationService } from 'rest-client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ActiveCampaignService } from '@app/modules/core/services/active-campaign/active-campaign.service';
import { GoogleTagManagerService } from '@app/modules/core/services/analytics/google-tag-manager.service';
import { AppState } from '@app/modules/core/states/app-state/app.state';
import { SourceAndQueryParams } from '@app/modules/core/states/trip-state/trip.model';
import { TripState } from '@app/modules/core/states/trip-state/trip.state';
import { DIALOG_CONFIG } from '@core/utils/dialog.config';
import { LoginDialogComponent } from '@shared/components/login-dialog/login-dialog.component';
import { SignUpDialogComponent } from '@shared/components/sign-up-dialog/sign-up-dialog.component';
import { ACCEPTED_COOKIE } from '@shared/const/cookies.consts';

@UntilDestroy()
@Component({
  selector: 'bkg-booking-main-page',
  templateUrl: './booking-main-page.component.html',
})
export class BookingMainPageComponent implements OnInit {
  @Select(AppState.isLoading) loadingData$: Observable<number>;
  cookiesAccepted = this.checkCookies();
  @Select(TripState.sourceAndQueryParams)
  sourceMode$: Observable<SourceAndQueryParams>;

  app_mode = false;
  client_app_mode = false;

  constructor(
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private googleTagManagerService: GoogleTagManagerService,
    private dialog: DialogService,
    private activeCampaignService: ActiveCampaignService
  ) { }

  ngOnInit(): void {
    this.subscribeToSourceData();
    if (
      !this.authService.getAccessToken() &&
      !this.hasModifyQueryParams() &&
      !this.hasAgentQueryParams() &&
      !this.hasFocusBillingParams() &&
      !this.hasAbsuid() && !this.isLinkBooking()
    ) {
      this.openLoginModal();
    }
  }

  onAcceptReturn(): void {
    this.googleTagManagerService.addGtmToDom();
    this.activeCampaignService.setAllowTracking();
    this.cookiesAccepted = true;
  }

  onRejectReturn(): void {
    this.googleTagManagerService.consentDisabled()
    Cookie.deleteAll();
    this.cookiesAccepted = false;
  }

  private openLoginModal(): void {
    this.dialog
      .open(LoginDialogComponent, {
        ...DIALOG_CONFIG,
        size: 'md',
        enableClose: false,
        data: { showContinue: true },
      })
      .afterClosed$.pipe(filter(Boolean))
      .pipe(untilDestroyed(this))
      .subscribe(() => this.openSignUpDialog());
  }

  private openSignUpDialog(): void {
    this.dialog
      .open(SignUpDialogComponent, {
        ...DIALOG_CONFIG,
        size: 'md',
        enableClose: false,
        data: { showContinue: true },
      })
      .afterClosed$.pipe(filter(Boolean))
      .pipe(untilDestroyed(this))
      .subscribe(() => this.openLoginModal());
  }

  private hasFocusBillingParams(): boolean {
    const focus = this.route.snapshot.queryParamMap.get('focus');

    return focus && focus === 'billing_data';
  }

  private hasAgentQueryParams(): boolean {
    const agent_res_token = this.route.snapshot.queryParamMap.get(
      'agent_res_token'
    );

    return !!agent_res_token;
  }

  private hasModifyQueryParams(): boolean {
    const modify_res_token = this.route.snapshot.queryParamMap.get(
      'modify_res_token'
    );

    return !!modify_res_token;
  }

  private hasAbsuid(): boolean {
    const absuid = this.route.snapshot.queryParamMap.get('absuid');

    return !!absuid;
  }

  private checkCookies(): boolean {
    switch (localStorage.getItem(ACCEPTED_COOKIE)) {
      case 'accept':
        return true;
      case 'reject':
        return false;
      default:
        return false;
    }
  }

  private subscribeToSourceData(): void {
    this.sourceMode$
      .pipe(untilDestroyed(this))
      .subscribe((sourceAndUp: SourceAndQueryParams) => {
        if (sourceAndUp.app_mode) {
          this.app_mode = true;
        }
        if (sourceAndUp.client_app_mode) {
          this.client_app_mode = true;
        }
      });
  }

  /** true if import_guest_reservation queryParam is present */
  private isLinkBooking(): boolean {
    const linkBooking = this.route.snapshot.queryParamMap.get(
      'import_guest_reservation'
    );

    return linkBooking == 'true';
  }
}
